/**
 * Utility for managing the chatbot's memory of user facts
 * 
 * This implementation stores facts in MongoDB using the chatMemoryService
 * Facts are stored per user without language-specific storage
 */

import * as chatMemoryService from './chatMemoryService';

// Maximum number of facts to store in total (enforced by the backend)
const MAX_MEMORY_FACTS = 150;

/**
 * Helper function to check if memory feature is enabled
 * @returns {boolean} - Whether memory is enabled
 */
const checkMemoryEnabled = () => {
  try {
    const memoryEnabled = localStorage.getItem('memoryEnabled') === 'true';
    console.log(`ChatMemoryManager - Memory feature check: ${memoryEnabled ? 'ENABLED' : 'DISABLED'}`);
    return memoryEnabled;
  } catch (error) {
    console.error('Error checking memory enabled status:', error);
    return false; // Default to disabled if there's an error
  }
};

/**
 * Initialize or retrieve chat memory from the API
 * @param {string} userId - The user's ID
 * @returns {Array} - The user's memory facts array with timestamps
 */
export const getMemory = async (userId) => {
  try {
    return await chatMemoryService.getMemory(userId);
  } catch (error) {
    console.error('Error retrieving memory:', error);
    return [];
  }
};

/**
 * Get memory facts for a specific user
 * @param {string} userId - The user's ID
 * @param {string} language - The language (not used currently but kept for future language-specific memory)
 * @returns {Array} - Array of fact strings
 */
export const getMemoryFacts = async (userId, language) => {
  try {
    console.log(`ChatMemoryManager - Retrieving memory facts for user: ${userId}`);
    const memoryEnabled = checkMemoryEnabled();
    
    if (!memoryEnabled) {
      console.log('ChatMemoryManager - Memory disabled, returning empty facts array');
      return [];
    }
    
    const memory = await getMemory(userId);
    console.log(`ChatMemoryManager - Retrieved ${memory.length} memory facts`);
    // Extract just the fact strings from the memory objects
    return memory.map(item => item.fact);
  } catch (error) {
    console.error('Error retrieving memory facts:', error);
    return [];
  }
};

/**
 * Add new facts to memory
 * @param {string} userId - The user's ID
 * @param {Array} newFacts - Array of fact strings to remember
 * @returns {Array} - Updated memory facts array
 */
export const addFacts = async (userId, newFacts) => {
  if (!newFacts || !Array.isArray(newFacts) || newFacts.length === 0) {
    return await getMemory(userId);
  }

  try {
    console.log(`ChatMemoryManager - Adding ${newFacts.length} facts for user: ${userId}`);
    const memoryEnabled = checkMemoryEnabled();
    
    if (!memoryEnabled) {
      console.log('ChatMemoryManager - Memory disabled, skipping fact storage');
      return [];
    }
    
    // Filter out empty facts
    const validFacts = newFacts.filter(fact => fact && fact.trim());
    
    if (validFacts.length === 0) {
      return await getMemory(userId);
    }

    console.log(`ChatMemoryManager - Storing ${validFacts.length} valid facts`);
    const result = await chatMemoryService.addFacts(userId, validFacts);
    console.log(`ChatMemoryManager - Memory update complete, total facts: ${result.length}`);
    return result;
  } catch (error) {
    console.error('Error adding facts:', error);
    return await getMemory(userId);
  }
};

/**
 * Get all facts from memory as a formatted string for the chatbot prompt
 * @param {string} userId - The user's ID
 * @returns {string} - Formatted memory string
 */
export const getFormattedMemory = async (userId) => {
  try {
    return await chatMemoryService.getFormattedMemory(userId);
  } catch (error) {
    console.error('Error formatting memory:', error);
    return '';
  }
};

/**
 * Get all facts as an array
 * @param {string} userId - The user's ID
 * @returns {Array} - Array of fact strings (without timestamps)
 */
export const getAllFacts = async (userId) => {
  try {
    return await chatMemoryService.getAllFacts(userId);
  } catch (error) {
    console.error('Error getting all facts:', error);
    return [];
  }
};

/**
 * Clear all memory for a user
 * @param {string} userId - The user's ID
 */
export const clearMemory = async (userId) => {
  try {
    await chatMemoryService.clearMemory(userId);
  } catch (error) {
    console.error('Error clearing memory:', error);
  }
};

// For backward compatibility with the existing code, provide synchronous versions
// that return empty results but log a warning
export const getMemorySync = (userId) => {
  console.warn('Using deprecated synchronous getMemory, use async version instead');
  // Instead of returning an empty array, trigger the async version and return the last value
  try {
    // Make a synchronous request to get facts (not ideal, but necessary for backward compatibility)
    const xhr = new XMLHttpRequest();
    xhr.open('GET', '/api/chat-memory/full', false); // false makes it synchronous
    xhr.setRequestHeader('Content-Type', 'application/json');
    // Add auth header if available
    const token = localStorage.getItem('firebase_token');
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }
    xhr.send();
    
    if (xhr.status === 200) {
      const response = JSON.parse(xhr.responseText);
      return response.memory || [];
    }
  } catch (e) {
    console.error('Sync memory retrieval failed:', e);
  }
  return [];
};

export const getAllFactsSync = (userId) => {
  console.warn('Using deprecated synchronous getAllFacts, use async version instead');
  // Instead of returning an empty array, trigger the async version and return the last value
  try {
    // Make a synchronous request to get facts (not ideal, but necessary for backward compatibility)
    const xhr = new XMLHttpRequest();
    xhr.open('GET', '/api/chat-memory/facts', false); // false makes it synchronous
    xhr.setRequestHeader('Content-Type', 'application/json');
    // Add auth header if available
    const token = localStorage.getItem('firebase_token');
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }
    xhr.send();
    
    if (xhr.status === 200) {
      const response = JSON.parse(xhr.responseText);
      return response.facts || [];
    }
  } catch (e) {
    console.error('Sync facts retrieval failed:', e);
  }
  return [];
};

export const getFormattedMemorySync = (userId) => {
  console.warn('Using deprecated synchronous getFormattedMemory, use async version instead');
  // Make a real attempt to get formatted memory synchronously
  try {
    const facts = getAllFactsSync(userId);
    
    if (!facts || facts.length === 0) {
      return '';
    }
    
    // Format facts for the prompt
    return `NOTABLE FACTS ABOUT THE USER:\n${facts.map(fact => `- ${fact}`).join('\n')}`;
  } catch (e) {
    console.error('Sync formatted memory retrieval failed:', e);
    return '';
  }
};

export const addFactsSync = (userId, newFacts) => {
  console.warn('Using deprecated synchronous addFacts, use async version instead');
  // Schedule the async version to run in the background
  addFacts(userId, newFacts).catch(console.error);
  return [];
};

export const clearMemorySync = (userId) => {
  console.warn('Using deprecated synchronous clearMemory, use async version instead');
  // Schedule the async version to run in the background
  clearMemory(userId).catch(console.error);
};

// Default export with both async and sync methods for backward compatibility
export default {
  // Async methods (new)
  getMemory,
  getMemoryFacts,
  addFacts,
  getFormattedMemory,
  getAllFacts,
  clearMemory,
  
  // Sync methods (deprecated)
  getMemorySync,
  addFactsSync,
  getFormattedMemorySync,
  getAllFactsSync,
  clearMemorySync
}; 